import { Component, inject, OnInit, signal, computed, effect, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject, catchError, finalize, map, of, takeUntil, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  DynamicFilterConfig,
  DynamicFilterValues,
  EnumValues,
  GlobalEntityDefinition,
  createDynamicFilterState,
} from '@card-software/library';
import { AuthService, debouncedServerSearch } from '@card-software/nexus-utilities';
import _ from 'lodash';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { PaginatorState } from 'primeng/paginator';
import { EnumService } from '../../../services/enum.service';
import { InventoryTransaction } from '../../../models/inventory/inventory-transaction';
import { KnittingOrder } from '../../../models/production/knitting-order';
import { TransferUnit, TransferUnitSearchCommand } from '../../../models/inventory/transfer-unit';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { KnittingOrderProxyService } from '../../../proxies/production/knitting-order.proxy.service';
import { TransferUnitProxyService } from '../../../proxies/inventory/transfer-unit.proxy.service';

@Component({
  selector: 'app-transfer-units',
  templateUrl: './transfer-units.component.html',
  styleUrl: './transfer-units.component.scss',
})
export class TransferUnitsComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private enumService = inject(EnumService);
  private translateService = inject(TranslateService);
  private readonly knittingOrderProxyService = inject(KnittingOrderProxyService);
  private readonly transferUnitProxyService = inject(TransferUnitProxyService);

  private destroy$ = new Subject<void>();
  private appliedFilters: DynamicFilterValues = {};
  isLoading = signal<number>(0);
  showFilters = signal<boolean>(true);
  transferUnits = signal<TransferUnit[]>([]);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'transfer-units-filters', config: this.filterConfig });

  listKnittingOrders = signal<KnittingOrder[]>([]);
  filteredKnittingOrders = signal<KnittingOrder[]>([]);

  // Tabs double as the status filter `search()` sends to the server (see its
  // `status` param below) — one shared table re-queried on tab change, not
  // four independent panels. `value` must match TransferUnitStatus's numeric
  // enum order (Draft/StandBy/InRoute/Received).
  readonly statusTabs = [
    { value: 0, key: 'Transfer_Units_Draft', fallback: 'Draft' },
    { value: 1, key: 'Transfer_Units_Stand_By', fallback: 'Stand By' },
    { value: 2, key: 'Transfer_Units_In_Route', fallback: 'In Route' },
    { value: 3, key: 'Transfer_Units_Received', fallback: 'Received' },
  ];

  private _selectedTab = signal<number>(0);
  transferUnitStatus = signal<EnumValues>({});
  enumsReady = signal(false);

  transferUnitInventoryTransactions: Record<string, InventoryTransaction[]> = {};

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalRecords = 0;

  canEdit = computed(() => {
    if (AuthService.hasRole(UserGroup.TransferUnitEditor) || AuthService.hasRole(UserGroup.TransferUnitAdmin)) {
      return true;
    }
    if (AuthService.hasRole(UserGroup.TransferUnitViewer) || AuthService.hasRole(UserGroup.TransferUnitReceiver)) {
      return false;
    }
    return false;
  });

  get selectedTab(): number {
    return this._selectedTab();
  }

  set selectedTab(value: number) {
    if (this._selectedTab() !== value) {
      this._selectedTab.set(value);
      this.currentPage = 1;
      this.search();
    }
  }

  getRowData(rowData: unknown): TransferUnit {
    return rowData as TransferUnit;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    effect(
      () => {
        const statusesValue = this.enumService.getEnum('TransferUnitStatus', 'inventory').values;
        if (Object.keys(statusesValue).length > 0) {
          this.transferUnitStatus.set(statusesValue);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    // The dynamic-filters panel renders the autocomplete, so there is no element for
    // ServerSearchDirective: the shared operator is wired by hand and released with the component.
    this.knittingOrderQueries$
      .pipe(
        debouncedServerSearch((query) => this.searchKnittingOrders(query)),
        takeUntil(this.destroy$),
      )
      .subscribe((orders) => this.filteredKnittingOrders.set(orders));

    this.loadInitialData();

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tryInitializeFilterConfig();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadInitialData(): void {
    // Initial search is handled by the filter state
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang && this.enumsReady()) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'searchByNumber',
          label: translate('Transfer_Units_Number', 'Transfer Unit Number'),
          placeholder: translate('Enter_transfer_unit_number', 'Enter transfer unit number'),
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'knittingOrder',
          label: translate('Knitting_Order', 'Knitting Order'),
          placeholder: translate('Select_knitting_order', 'Select knitting order'),
          autocompleteConfig: {
            suggestions: this.filteredKnittingOrders(),
            optionLabel: 'knittingOrderNumber',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.knittingOrderQueries$.next(event.query),
            resolveById: (id: string) => this.resolveKnittingOrder(id),
            dropdown: true,
          },
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private resolveKnittingOrder(id: string): Observable<KnittingOrder | null> {
    return this.knittingOrderProxyService.getById(id).pipe(
      map((response) => response.data ?? null),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private readonly knittingOrderQueries$ = new Subject<string>();

  private searchKnittingOrders(query: string): Observable<KnittingOrder[]> {
    return this.knittingOrderProxyService.search({ knittingOrderNumber: query }).pipe(
      finalize(() => this.stopLoading()),
      map((result) => result?.data?.items || []),
    );
  }

  search() {
    const status = Number(this._selectedTab());
    const formValues = this.appliedFilters;
    const searchTerm = (formValues['searchByNumber'] as string | undefined)?.trim();
    const knittingOrder = formValues['knittingOrder'] as string | { id?: string } | null | undefined;
    const knittingOrderId = typeof knittingOrder === 'string' ? knittingOrder : knittingOrder?.id;

    const command: TransferUnitSearchCommand = {
      statuses: [status],
      searchTerm: searchTerm || undefined,
      initiator: knittingOrderId
        ? {
            entityDefinitionCode: GlobalEntityDefinition.KnittingOrder,
            reference: knittingOrderId,
          }
        : undefined,
    };

    this.startLoading();
    this.transferUnitProxyService
      .search(command, this.currentPage, this.pageSize)
      .pipe(
        finalize(() => {
          this.stopLoading();
        }),
      )
      .subscribe({
        next: (result) => {
          this.transferUnits.set(result?.data?.items || []);
          this.totalRecords = result?.data?.totalCount || 0;
        },
        error: () => {
          this.transferUnits.set([]);
          this.totalRecords = 0;
        },
      });
  }

  onPageChange(event: PaginatorState) {
    this.currentPage = (event.page || 0) + 1;
    this.pageSize = event.rows || this.pageSize;
    this.search();
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    this.appliedFilters = filters;
    this.currentPage = 1;
    this.search();
  }

  redirectToDetails(id?: string): void {
    const targetId = id ?? 'new';
    this.router.navigate(['transfer-unit-details'], { queryParams: { id: targetId } });
  }

  getRollNumbers(transferUnit: TransferUnit): string {
    const inventoryTransaction =
      !_.isNil(transferUnit.inventoryTransactions) && !_.isEmpty(transferUnit.inventoryTransactions)
        ? transferUnit.inventoryTransactions[0]
        : undefined;
    return inventoryTransaction?.lines.map((line) => line?.lotItemKey?.number).join(', ') || '';
  }
}
